import { apiGet, type QueryRecord } from './http-client';

export type PublicWidgetBranch = {
  id: number;
  name: string;
  address: string | null;
  photo_url: string | null;
  working_hours: string | null;
  phone: string | null;
  /** Настройка филиала: показывать поле «комментарий» на шаге записи (по умолчанию выкл.). */
  client_booking_comment_enabled?: boolean;
};

export type PublicWidgetResponse = {
  organization_id: number;
  name: string;
  slug: string;
  /** Фото организации из CRM — фон баннера шапки. */
  photo_url?: string | null;
  /** Логотип бренда (круг) — рядом с названием. */
  brand_logo_url?: string | null;
  /** Сеть филиалов — бейдж в шапке. */
  is_network?: boolean;
  /** Краткое описание организации (как в CRM), с форматом хранения. */
  description?: string | null;
  description_format?: 'html' | 'markdown' | 'plain';
  working_hours?: string | null;
  /** Нормализованный телефон для отображения и tel:-ссылки. */
  phone?: string | null;
  /** Код типа организации из глобального справочника. */
  organization_type?: string | null;
  /** Человекочитаемая подпись типа (из глобальных настроек). */
  organization_type_label?: string | null;
  /** Подпись точек в интерфейсе (мн.ч.), напр. «Залы», «Салоны». */
  branch_label_plural?: string;
  /** Подпись одной точки (ед.ч.), напр. «Зал», «Салон». */
  branch_label_singular?: string;
  widget: Record<string, unknown>;
  branches: PublicWidgetBranch[];
  widget_name: string;
  trainer_widget_name: string;
  /** См. ADR-0012 / design: бренд-embed — true только при действующем тарифе и флаге плана. */
  brand_embed_allowed: boolean;
  /** Часовой пояс организации (IANA), для подсказок «открыто» в карточках филиалов. */
  timezone?: string;
  /**
   * Можно оформить запись с оплатой в студии (см. POST /bookings payment_method=cash), если в организации
   * включены наличные оффлайн за занятия.
   */
  client_booking_cash_at_studio_enabled?: boolean;
  /** Время на онлайн-оплату в минутах (effective; default 15). */
  payment_ttl_minutes?: number;
};

/** GET /api/v1/public/widget/{slug} */
export function fetchPublicWidgetConfig(
  slug: string,
  query: QueryRecord
): Promise<PublicWidgetResponse> {
  return apiGet<PublicWidgetResponse>(`/api/v1/public/widget/${encodeURIComponent(slug)}`, query);
}

/** GET /api/v1/public/widget-global — настройки глобального приложения (без org_slug в URL). */
export type GlobalWidgetConfigResponse = {
  title: string;
  button_text: string;
  primary_color: string;
  /** light | dark | system */
  embed_theme: string;
};

export function fetchGlobalWidgetConfig(): Promise<GlobalWidgetConfigResponse> {
  return apiGet<GlobalWidgetConfigResponse>('/api/v1/public/widget-global');
}

/** Филиал в ответе GET /public/organizations-with-branches */
export type PublicOrganizationBranchListItem = {
  id: number;
  name: string;
  address: string | null;
  photo_url: string | null;
  working_hours: string | null;
  phone: string | null;
};

/** Организация для витрины глобального виджета (публичные поля). */
export type PublicOrganizationWithBranchesItem = {
  id: number;
  name: string;
  slug: string;
  photo_url: string | null;
  brand_logo_url?: string | null;
  description: string | null;
  working_hours?: string | null;
  phone?: string | null;
  org_type: string | null;
  branch_label_plural?: string;
  branch_label_singular?: string;
  branches_count: number;
  branches: PublicOrganizationBranchListItem[];
  /** Часовой пояс организации (IANA). */
  timezone?: string;
  /** Можно оформить запись с оплатой в студии. */
  client_booking_cash_at_studio_enabled?: boolean;
  /** Время на онлайн-оплату в минутах (effective; default 15). */
  payment_ttl_minutes?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings?: Record<string, any>;
};

/** GET /api/v1/public/organizations-with-branches — каталог для гостевого выбора без JWT. */
export function fetchPublicOrganizationsWithBranches(): Promise<PublicOrganizationWithBranchesItem[]> {
  return apiGet<PublicOrganizationWithBranchesItem[]>('/api/v1/public/organizations-with-branches');
}
